import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Shield, Lock, Users, Bell, Image, FileText, Zap, ArrowRight,
  Check, Globe, Eye, EyeOff, Server, Fingerprint, KeyRound, ArrowLeft, X
} from 'lucide-react';

export default function WallInfoPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Lock,
      title: 'End-to-End Encrypted Posts',
      description: 'Every post on your wall is protected with AES-256 encryption. Your content stays private between you and your subscribers — not even we can read it.',
      color: 'from-emerald-500 to-emerald-700'
    },
    {
      icon: Users,
      title: 'Follower & Subscriber System',
      description: 'Choose who sees your content. Followers get access to your wall. Subscribers get real-time push notifications when you post — full control over your audience.',
      color: 'from-[#6D4AFF] to-[#9D7AFF]'
    },
    {
      icon: Image,
      title: 'Rich Media Uploads',
      description: 'Share photos, videos, PDFs, and documents directly on your wall. Drag-and-drop uploads with progress tracking. All files stored in your encrypted cloud vault.',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      icon: Bell,
      title: 'Instant Device Notifications',
      description: 'Subscribers receive real-time push notifications on every new post. Stay connected with your community across all devices — desktop and mobile.',
      color: 'from-amber-500 to-orange-600'
    },
    {
      icon: Globe,
      title: 'Custom Wall URL',
      description: 'Claim your personal wall address like mypatriotmail.org/wall/j.smith. Your identity, your brand, your corner of the internet.',
      color: 'from-pink-500 to-rose-600'
    },
    {
      icon: Shield,
      title: 'Zero Data Harvesting',
      description: 'Unlike mainstream platforms, we never mine your posts for ads, sell your data, or build profiles on your subscribers. Your wall is yours alone.',
      color: 'from-red-500 to-red-700'
    },
  ];

  const useCases = [
    {
      title: 'Community Leaders',
      description: 'Broadcast updates, organize events, and keep your community informed — all within a trusted, encrypted network.',
      icon: '🏛️'
    },
    {
      title: 'Small Businesses',
      description: 'Share product updates, announcements, and behind-the-scenes content with your customer base. No algorithms hiding your posts.',
      icon: '🏪'
    },
    {
      title: 'Content Creators',
      description: 'Post your content where it matters. No censorship, no shadowbans, no engagement manipulation. Your subscribers see everything you post.',
      icon: '🎬'
    },
    {
      title: 'Families & Groups',
      description: 'Share photos, updates, and memories with family and close friends in a private, secure space. No strangers, no ads.',
      icon: '👨‍👩‍👧‍👦'
    },
    {
      title: 'Researchers & Writers',
      description: 'Publish findings, articles, and updates to a verified audience. Keep an encrypted record of your published work.',
      icon: '📝'
    },
    {
      title: 'Faith Organizations',
      description: 'Share sermons, updates, prayer requests, and community news privately. Keep your congregation connected and informed.',
      icon: '⛪'
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a18] text-white">
      {/* Navigation */}
      <header className="fixed top-0 w-full z-50 border-b border-[#2d2d44]/50 bg-[#0a0a18]/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/mpm-logo.png" alt="MyPatriotMail" className="w-8 h-8 object-contain" />
            <span className="text-lg font-bold">MyPatriotMail</span>
          </button>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
            >
              Get Started <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-[#6D4AFF]/10 rounded-full blur-[100px]" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/30 mb-8">
            <Lock className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-400 font-medium">Encrypted. Private. Yours.</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight mb-6 leading-tight">
            <span className="text-white">My Patriot </span>
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Wall</span>
          </h1>

          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            Your personal encrypted broadcast channel. Share updates, photos, and files with your subscribers — 
            like your own <span className="text-white font-medium">private social network</span> inside the most secure email platform in America.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button
              onClick={() => navigate('/register')}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold px-8 py-6 text-lg rounded-xl shadow-[0_0_30px_rgba(16,185,129,0.3)]"
              data-testid="wall-info-cta"
            >
              <Zap className="w-5 h-5 mr-2" /> Start Your Wall Free
            </Button>
            <Button
              onClick={() => navigate('/wall-demo')}
              variant="outline"
              size="lg"
              className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-8 py-6 text-lg rounded-xl"
              data-testid="wall-info-demo-btn"
            >
              <Eye className="w-5 h-5 mr-2" /> See Live Demo
            </Button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-emerald-500" /> AES-256 Encrypted</span>
            <span className="flex items-center gap-1.5"><EyeOff className="w-4 h-4 text-emerald-500" /> Zero Data Mining</span>
            <span className="flex items-center gap-1.5"><Server className="w-4 h-4 text-emerald-500" /> American Servers</span>
            <span className="flex items-center gap-1.5"><Fingerprint className="w-4 h-4 text-emerald-500" /> No Tracking</span>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            How It <span className="text-emerald-400">Works</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-lg mx-auto">Three steps to your own encrypted broadcast channel</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Create Your Wall', desc: 'Pick a name, claim your custom URL, and set up your encrypted wall in seconds.' },
              { step: '02', title: 'Post & Upload', desc: 'Share text updates, photos, videos, and files. All media is encrypted and stored in your private vault.' },
              { step: '03', title: 'Grow Your Audience', desc: 'Others subscribe to your wall. Subscribers get notifications. You control who sees what.' },
            ].map((item, i) => (
              <div key={i} className="relative text-center p-6">
                <div className="text-6xl font-black text-emerald-500/10 mb-2">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-2 -mt-4">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-[#0d0d1a]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            Built for <span className="text-emerald-400">Privacy-First</span> Publishing
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-lg mx-auto">Every feature designed with encryption and your freedom in mind</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div
                key={i}
                className="p-6 bg-[#1a1a2e]/80 border border-[#2d2d44] rounded-2xl hover:border-emerald-500/30 transition-all group"
                data-testid={`wall-feature-${i}`}
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            Who Is This <span className="text-emerald-400">For?</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-lg mx-auto">My Patriot Wall empowers real people with real privacy</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((uc, i) => (
              <div key={i} className="p-5 bg-[#1a1a2e]/60 border border-[#2d2d44] rounded-xl hover:border-emerald-500/20 transition-all">
                <span className="text-3xl mb-3 block">{uc.icon}</span>
                <h3 className="text-lg font-bold text-white mb-1">{uc.title}</h3>
                <p className="text-sm text-gray-400">{uc.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-20 px-4 bg-[#0d0d1a]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            Why Not Just Use <span className="line-through text-gray-600">Social Media</span>?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-6 bg-red-500/5 border border-red-500/20 rounded-2xl">
              <h3 className="text-lg font-bold text-red-400 mb-4">Other Platforms</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                {[
                  'Your data sold to advertisers',
                  'Algorithms control who sees your posts',
                  'Content censored without explanation',
                  'Tracking pixels in every interaction',
                  'Your content used to train AI models',
                  'Account suspended for wrong opinions',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl">
              <h3 className="text-lg font-bold text-emerald-400 mb-4">My Patriot Wall</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                {[
                  'Your data never sold, never shared',
                  'Every subscriber sees every post — no algorithm',
                  'Your content, your rules, no censorship',
                  'Zero tracking, zero analytics harvesting',
                  'AES-256 encrypted from post to storage',
                  'American-owned, American-hosted servers',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="p-10 bg-gradient-to-br from-emerald-500/10 to-[#6D4AFF]/10 border border-emerald-500/20 rounded-3xl">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Own Your <span className="text-emerald-400">Voice</span>?
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Create your encrypted wall today. Free with any qualifying plan. No ads, no tracking, no compromises.
            </p>
            <Button
              onClick={() => navigate('/register')}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-black font-bold px-10 py-6 text-lg rounded-xl shadow-[0_0_40px_rgba(16,185,129,0.3)]"
            >
              <Zap className="w-5 h-5 mr-2" /> Create My Wall
            </Button>
            <p className="mt-4">
              <button onClick={() => navigate('/wall-demo')} className="text-emerald-400 hover:text-emerald-300 text-sm underline underline-offset-4">
                or see the live demo first
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#2d2d44]/50 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/mpm-logo.png" alt="MyPatriotMail" className="w-8 h-8 object-contain" />
            <span className="font-bold">MyPatriotMail</span>
          </div>
          <div className="flex gap-6 text-sm text-gray-500">
            <button onClick={() => navigate('/privacy')} className="hover:text-emerald-400">Privacy</button>
            <button onClick={() => navigate('/terms')} className="hover:text-emerald-400">Terms</button>
            <button onClick={() => navigate('/roadmap')} className="hover:text-emerald-400">Roadmap</button>
            <button onClick={() => navigate('/')} className="hover:text-emerald-400">Home</button>
          </div>
          <p className="text-gray-600 text-sm">© 2024-2026 MyPatriotMail</p>
        </div>
      </footer>
    </div>
  );
}
